import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const PricingSection = ({ formData, onChange, onCheckboxChange }) => {
  const customOptions = formData?.customOptions || [];
  const customOptionsType = formData?.customOptionsType || 'multiple';

  // Ticket option toggles (default true for own/rental, false for standard)
  const allowOwnBoard = formData?.allowOwnBoard !== false;
  const allowRentalBoard = formData?.allowRentalBoard !== false;
  const allowStandardTicket = formData?.allowStandardTicket === true;

  const handleAddOption = () => {
    const updated = [...customOptions, { title: '', price: '' }];
    onCheckboxChange('customOptions', updated);
  };

  const handleRemoveOption = (index) => {
    const updated = customOptions?.filter((_, i) => i !== index);
    onCheckboxChange('customOptions', updated);
  };

  const handleOptionChange = (index, field, value) => {
    const updated = customOptions?.map((opt, i) =>
    i === index ? { ...opt, [field]: value } : opt
    );
    onCheckboxChange('customOptions', updated);
  };

  return (
    <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-organic">
      <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-5 lg:mb-6">
        Fiyatlandırma & Biletler
      </h2>
      <div className="space-y-6 md:space-y-7 lg:space-y-8">

        {/* ── Katılım & Bilet Seçenekleri ── */}
        <div className="border border-border rounded-xl p-4 md:p-5 bg-muted/20">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
            <Icon name="Ticket" size={18} className="text-primary" />
            Katılım &amp; Bilet Seçenekleri
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Bu etkinlikte sunulacak katılım seçeneklerini, fiyatlarını ve açıklamalarını belirleyin.
          </p>

          <div className="space-y-4">
            {/* Own Board */}
            <div className={`rounded-lg border-2 p-4 transition-colors duration-200 ${allowOwnBoard ? 'border-primary/40 bg-primary/5' : 'border-border bg-background'}`}>
              <div className="flex items-start gap-3 mb-3">
                <input
                  type="checkbox"
                  id="allowOwnBoard"
                  checked={allowOwnBoard}
                  onChange={(e) => onCheckboxChange('allowOwnBoard', e?.target?.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer" />

                <label htmlFor="allowOwnBoard" className="cursor-pointer flex-1">
                  <span className="block text-sm font-semibold text-foreground">Kendi Board'u İle Katılım</span>
                </label>
              </div>
              {allowOwnBoard &&
              <div className="ml-7 space-y-3">
                  <Input
                  label="Fiyat (₺)"
                  type="number"
                  name="priceOwnBoard"
                  placeholder="150"
                  value={formData?.priceOwnBoard}
                  onChange={onChange}
                  min="0"
                  step="10" />

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Açıklama / Alt Metin</label>
                    <input
                    type="text"
                    name="descOwnBoard"
                    placeholder="Örn: Kendi SUP board'unuzu getirin"
                    value={formData?.descOwnBoard || ''}
                    onChange={onChange}
                    className="w-full h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground" />

                  </div>
                </div>
              }
            </div>

            {/* Rental Board */}
            <div className={`rounded-lg border-2 p-4 transition-colors duration-200 ${allowRentalBoard ? 'border-secondary/40 bg-secondary/5' : 'border-border bg-background'}`}>
              <div className="flex items-start gap-3 mb-3">
                <input
                  type="checkbox"
                  id="allowRentalBoard"
                  checked={allowRentalBoard}
                  onChange={(e) => onCheckboxChange('allowRentalBoard', e?.target?.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer" />

                <label htmlFor="allowRentalBoard" className="cursor-pointer flex-1">
                  <span className="block text-sm font-semibold text-foreground">Board Kiralama Dahil Katılım</span>
                </label>
              </div>
              {allowRentalBoard &&
              <div className="ml-7 space-y-3">
                  <Input
                  label="Fiyat (₺)"
                  type="number"
                  name="priceWithRental"
                  placeholder="250"
                  value={formData?.priceWithRental}
                  onChange={onChange}
                  min="0"
                  step="10" />

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Açıklama / Alt Metin</label>
                    <input
                    type="text"
                    name="descRentalBoard"
                    placeholder="Örn: Ekipman kiralama dahil, sadece gelin"
                    value={formData?.descRentalBoard || ''}
                    onChange={onChange}
                    className="w-full h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground" />

                  </div>
                </div>
              }
            </div>

            {/* Standard Ticket (No Board) */}
            <div className={`rounded-lg border-2 p-4 transition-colors duration-200 ${allowStandardTicket ? 'border-amber-400/60 bg-amber-50/50' : 'border-border bg-background'}`}>
              <div className="flex items-start gap-3 mb-3">
                <input
                  type="checkbox"
                  id="allowStandardTicket"
                  checked={allowStandardTicket}
                  onChange={(e) => onCheckboxChange('allowStandardTicket', e?.target?.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer" />

                <label htmlFor="allowStandardTicket" className="cursor-pointer flex-1">
                  <span className="block text-sm font-semibold text-foreground">Standart Etkinlik Bileti </span>
                </label>
              </div>
              {allowStandardTicket &&
              <div className="ml-7 space-y-3">
                  <Input
                  label="Fiyat (₺)"
                  type="number"
                  name="priceStandardTicket"
                  placeholder="50"
                  value={formData?.priceStandardTicket || ''}
                  onChange={onChange}
                  min="0"
                  step="10" />

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Açıklama / Alt Metin</label>
                    <input
                    type="text"
                    name="descStandardTicket"
                    placeholder="Örn: Board olmadan etkinliğe katılım"
                    value={formData?.descStandardTicket || ''}
                    onChange={onChange}
                    className="w-full h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground" />

                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
          <Input
            label="Can Yeleği Ücreti (TL)"
            type="number"
            name="priceLifeVest"
            placeholder="0"
            value={formData?.priceLifeVest}
            onChange={onChange}
            min="0"
            step="5"
            description="Opsiyonel ekipman ücreti" />

          <Input
            label="SUP Motoru Ücreti (TL)"
            type="number"
            name="priceSupMotor"
            placeholder="0"
            value={formData?.priceSupMotor}
            onChange={onChange}
            min="0"
            step="10"
            description="Opsiyonel ekipman ücreti" />

          <Input
            label="Su Geçirmez Çanta Ücreti (TL)"
            type="number"
            name="priceWaterproofBag"
            placeholder="0"
            value={formData?.priceWaterproofBag}
            onChange={onChange}
            min="0"
            step="5"
            description="Opsiyonel ekipman ücreti" />
        </div>

        {/* Dynamic Custom Options */}
        <div>
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <label className="block text-sm md:text-base font-medium text-foreground">
              Ekstra Seçenekler
            </label>
            <button
              type="button"
              onClick={handleAddOption}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors duration-200">

              <Icon name="Plus" size={16} />
              Yeni Ekstra Seçenek Ekle
            </button>
          </div>

          {/* Selection Type Toggle */}
          {customOptions?.length > 0 &&
          <div className="mb-4 p-3 bg-muted/40 rounded-lg border border-border">
              <label className="block text-sm font-medium text-foreground mb-2">
                Seçim Tipi
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex items-center gap-2 cursor-pointer flex-1 p-2.5 rounded-lg border-2 transition-colors duration-200"
              style={{ borderColor: customOptionsType === 'single' ? 'var(--primary)' : 'var(--border)', background: customOptionsType === 'single' ? 'var(--primary)/10' : 'transparent' }}>
                  <input
                  type="radio"
                  name="customOptionsType"
                  value="single"
                  checked={customOptionsType === 'single'}
                  onChange={() => onCheckboxChange('customOptionsType', 'single')}
                  className="w-4 h-4 text-primary border-border focus:ring-primary cursor-pointer" />

                  <div>
                    <span className="text-sm font-semibold text-foreground block">Tekli Seçim (Radio)</span>
                    <span className="text-xs text-muted-foreground">Kullanıcı sadece birini seçebilir</span>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer flex-1 p-2.5 rounded-lg border-2 transition-colors duration-200"
              style={{ borderColor: customOptionsType === 'multiple' ? 'var(--primary)' : 'var(--border)', background: customOptionsType === 'multiple' ? 'var(--primary)/10' : 'transparent' }}>
                  <input
                  type="radio"
                  name="customOptionsType"
                  value="multiple"
                  checked={customOptionsType === 'multiple'}
                  onChange={() => onCheckboxChange('customOptionsType', 'multiple')}
                  className="w-4 h-4 text-primary border-border focus:ring-primary cursor-pointer" />

                  <div>
                    <span className="text-sm font-semibold text-foreground block">Çoklu Seçim (Checkbox)</span>
                    <span className="text-xs text-muted-foreground">Kullanıcı birden fazla seçebilir</span>
                  </div>
                </label>
              </div>
            </div>
          }

          {customOptions?.length === 0 &&
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-4 text-center border border-dashed border-border">
              Henüz ekstra seçenek eklenmedi. "Yeni Ekstra Seçenek Ekle" butonuna tıklayarak ekleyebilirsiniz.
            </div>
          }

          <div className="space-y-3">
            {customOptions?.map((option, index) =>
            <div key={index} className="flex items-center gap-3 bg-muted/30 rounded-lg p-3 border border-border">
                <div className="flex-1">
                  <input
                  type="text"
                  value={option?.title || ''}
                  onChange={(e) => handleOptionChange(index, 'title', e?.target?.value)}
                  placeholder="Seçenek başlığı (Örn: VIP Transfer)"
                  className="w-full h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground" />

                </div>
                <div className="w-28">
                  <input
                  type="number"
                  value={option?.price || ''}
                  onChange={(e) => handleOptionChange(index, 'price', e?.target?.value)}
                  placeholder="Fiyat (₺)"
                  min="0"
                  className="w-full h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground" />

                </div>
                <button
                type="button"
                onClick={() => handleRemoveOption(index)}
                className="w-8 h-8 flex items-center justify-center text-error hover:bg-error/10 rounded-md transition-colors flex-shrink-0">

                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>);

};

export default PricingSection;